import { useMemo, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { ListingHeader } from "./listing/ListingHeader";
import { ListingFooter } from "./listing/ListingFooter";
import { MobileContactBar } from "./listing/MobileContactBar";
import { AgentCard } from "./listing/AgentCard";
import { PhotoLightbox } from "./listing/PhotoLightbox";
import { ShowingRequestDialog } from "./listing/ShowingRequestDialog";
import { useListingData, listingFileUrl } from "./listing/useListingData";
import { useHashScroll } from "./listing/useHashScroll";
import { LISTING_STATUS_LABELS } from "./listing/constants";
import { FACT_ROWS, hoaLineFor, DetailsPhotosSection, FloorplanSection, VideoSection, TourSection, FactsSection, HighlightsSection, DescriptionSection } from "./listing/PropertyWebsiteDetailsSections";

const protectImage = (event) => {
  if (event.target instanceof Element && event.target.closest("img, video")) event.preventDefault();
};

export default function PropertyWebsiteDetails() {
  const { slug, preview, listing, error } = useListingData();
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [showingOpen, setShowingOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const images = useMemo(() => (listing?.files || []).filter((file) => file.is_image), [listing]);
  const ordered = useMemo(() => {
    if (!images.length) return [];
    const cover = images.find((file) => file.id === listing?.cover_file_id);
    return cover ? [cover, ...images.filter((file) => file.id !== cover.id)] : images;
  }, [images, listing]);
  const fileUrl = (file) => listingFileUrl(listing, slug, preview, file);
  const floorplanUrl = listing?.floorplan_file_id ? fileUrl({ id: listing.floorplan_file_id }) : null;

  useHashScroll(Boolean(listing));

  if (error) return <main data-testid="public-listing-error" className="min-h-screen bg-white grid place-items-center p-8 text-center"><h1 className="text-xl font-serif-display text-[#202833]">{error}</h1></main>;
  if (!listing) return <main data-testid="public-listing-loading" className="min-h-screen grid place-items-center bg-white font-mono text-sm text-[#667085]">Loading property details…</main>;

  const contact = listing.contact;
  const brandLabel = contact?.name || listing.full_address || listing.project_name;
  const facts = FACT_ROWS.filter(([key]) => listing[key]);
  const hoaLine = hoaLineFor(listing);

  return (
    <main data-testid="public-property-details" onContextMenuCapture={protectImage} onDragStartCapture={protectImage} className="min-h-screen bg-white text-[#202833]">
      {preview && <div data-testid="public-listing-preview-banner" className="bg-[#C7A361] text-[#111820] text-center text-xs font-mono uppercase tracking-[0.14em] py-2">Admin preview — this is how the page will look to visitors</div>}
      <ListingHeader slug={slug} brandLabel={brandLabel} preview={preview} onRequestShowing={() => setShowingOpen(true)} onMenuOpenChange={setMobileMenuOpen} />

      <section className="bg-[#111820] text-white py-10 sm:py-14">
        <div className="mx-auto max-w-[1360px] px-4 sm:px-6 lg:px-10">
          {listing.listing_status && <span data-testid="public-details-status" className="inline-block rounded-full bg-[#C7A361] text-[#111820] text-[11px] font-semibold uppercase tracking-[0.1em] px-3 py-1 mb-3">{LISTING_STATUS_LABELS[listing.listing_status] || listing.listing_status}</span>}
          <h1 data-testid="public-details-title" className="font-serif-display text-2xl sm:text-3xl">{listing.full_address || listing.project_name}</h1>
          {listing.price && <p data-testid="public-details-price" className="mt-2 font-serif-display text-xl text-[#C7A361]">{listing.price}</p>}
        </div>
      </section>

      <div className="mx-auto max-w-[1360px] px-4 sm:px-6 lg:px-10 py-10 sm:py-14 space-y-14 sm:space-y-20">
        <DetailsPhotosSection ordered={ordered} fileUrl={fileUrl} onViewPhoto={setLightboxIndex} />

        <FloorplanSection floorplanUrl={floorplanUrl} />

        <VideoSection video={listing.video} fileUrl={fileUrl} />

        <TourSection tourUrl={listing.tour_url} />

        <section className="grid lg:grid-cols-[1fr_320px] gap-10">
          <div className="space-y-10 min-w-0">
            <FactsSection facts={facts} hoaLine={hoaLine} listing={listing} />
            <HighlightsSection highlights={listing.highlights} />
            <DescriptionSection description={listing.website_description} />
          </div>
          <AgentCard contact={contact} onRequestShowing={() => setShowingOpen(true)} sticky />
        </section>
      </div>

      <ListingFooter slug={slug} brandLabel={brandLabel} brokerage={contact?.company} preview={preview} />
      <MobileContactBar phone={contact?.phone} onRequestShowing={() => setShowingOpen(true)} hidden={lightboxIndex !== null || showingOpen || mobileMenuOpen} />

      <AnimatePresence>
        {lightboxIndex !== null && (
          <PhotoLightbox photos={ordered.map((f) => ({ id: f.id, url: fileUrl(f) }))} index={lightboxIndex} onIndexChange={setLightboxIndex} onClose={() => setLightboxIndex(null)} />
        )}
      </AnimatePresence>
      <ShowingRequestDialog open={showingOpen} onClose={() => setShowingOpen(false)} slug={slug} agentName={contact?.name} address={listing.full_address || listing.project_name} />
    </main>
  );
}
